import { LABOR_BASES, LABOR_BASIS_LABELS, type LaborBasis, type LaborItem } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { errorMessage, get, patch, post, put } from "../../api";
import { Alert, Button, Checkbox, Field, Loading, PageHeader, Panel, PanelHeader, SelectField, cx, formatCents, parseDollars } from "../../components/ui";
import { useSettings } from "./useSettings";

/** Settings → Work orders: the labor price list, the response window, and paperwork reminders. */
export function WorkOrderSettings() {
  return (
    <div className="flex flex-col gap-5">
      <PageHeader title="Work orders, subs, and weather" />
      <LaborPriceList />
      <Timing />
      <Weather />
    </div>
  );
}

function Timing() {
  const { query, save } = useSettings();
  const [hours, setHours] = useState("");
  const [days, setDays] = useState("");
  const [escalate, setEscalate] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    const s = query.data?.settings;
    if (s && !hours) {
      setHours(String(s["workOrders.responseHours"]));
      setDays(String(s["compliance.reminderDays"]));
      setEscalate(s["tasks.escalateAfterDays"] ? String(s["tasks.escalateAfterDays"]) : "");
    }
  }, [query.data, hours]);
  if (query.isLoading) return <Loading />;
  return (
    <Panel>
      <PanelHeader title="Timing" />
      <form
        className="flex flex-col gap-4 px-5 py-5"
        onSubmit={(e) => {
          e.preventDefault();
          setNotice(null);
          save.mutate({ "workOrders.responseHours": Number(hours), "compliance.reminderDays": Number(days), "tasks.escalateAfterDays": escalate.trim() ? Number(escalate) : null }, { onSuccess: () => setNotice("Saved.") });
        }}
      >
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        {notice && <Alert tone="ok">{notice}</Alert>}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Flag a work order with no answer after (hours)" type="number" min={1} max={336} value={hours} onChange={(e) => setHours(e.target.value)} />
          <Field label="Warn about expiring paperwork (days ahead)" type="number" min={1} max={120} value={days} onChange={(e) => setDays(e.target.value)} />
          <Field label="Escalate tasks this many days overdue (blank: never)" type="number" min={1} max={60} value={escalate} onChange={(e) => setEscalate(e.target.value)} hint="Raised to high, and the first admin gets a copy." />
        </div>
        <div>
          <Button type="submit" variant="primary" busy={save.isPending}>
            Save
          </Button>
        </div>
      </form>
    </Panel>
  );
}

function LaborPriceList() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["labor-items"], queryFn: () => get<LaborItem[]>("/api/labor-items") });
  const [name, setName] = useState("");
  const [unit, setUnit] = useState("SQ");
  const [basis, setBasis] = useState<LaborBasis>("manual");
  const [price, setPrice] = useState("");
  const refresh = () => qc.invalidateQueries({ queryKey: ["labor-items"] });
  const cents = parseDollars(price);
  const add = useMutation({
    mutationFn: () => post<LaborItem>("/api/labor-items", { name, unit, basis, priceCents: cents }),
    onSuccess: async () => {
      setName("");
      setPrice("");
      await refresh();
    },
  });
  const change = useMutation({ mutationFn: ({ id, ...body }: { id: string } & Partial<LaborItem>) => patch<LaborItem>(`/api/labor-items/${id}`, body), onSuccess: refresh });
  const move = useMutation({ mutationFn: (ids: string[]) => put("/api/labor-items/order", { ids }), onSuccess: refresh });
  const items = q.data ?? [];
  return (
    <Panel>
      <PanelHeader title="Labor price list" description="What we pay subs: base price per square, steep charges, extra layers, vents... Pick these when pricing a work order. Items counted from a measurement report fill in on their own (subs can have their own rates on their contact page)." />
      {(add.error || change.error) && (
        <div className="px-5 pt-4">
          <Alert>{errorMessage(add.error ?? change.error)}</Alert>
        </div>
      )}
      {q.isLoading ? (
        <div className="px-5">
          <Loading />
        </div>
      ) : (
        <ul>
          {items.map((it, i) => (
            <LaborRow
              key={it.id}
              item={it}
              onSave={(body) => change.mutate({ id: it.id, ...body })}
              onUp={i > 0 ? () => move.mutate(items.map((x, k) => (k === i - 1 ? it.id : k === i ? items[i - 1]!.id : x.id))) : undefined}
            />
          ))}
        </ul>
      )}
      <form
        className="grid grid-cols-2 items-end gap-2 border-t border-mist px-5 py-4 md:grid-cols-[1fr_80px_190px_110px_auto]"
        onSubmit={(e) => {
          e.preventDefault();
          add.mutate();
        }}
      >
        <Field label="Add item" value={name} onChange={(e) => setName(e.target.value)} placeholder="Tear off and install" />
        <Field label="Unit" value={unit} onChange={(e) => setUnit(e.target.value)} />
        <SelectField label="Counted by" value={basis} onChange={(e) => setBasis(e.target.value as LaborBasis)}>
          {LABOR_BASES.map((b) => (
            <option key={b} value={b}>
              {LABOR_BASIS_LABELS[b]}
            </option>
          ))}
        </SelectField>
        <Field label="Price" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="75.00" />
        <Button type="submit" busy={add.isPending} disabled={!name.trim() || cents === null || Number.isNaN(cents)}>
          Add
        </Button>
      </form>
    </Panel>
  );
}

function LaborRow({ item, onSave, onUp }: { item: LaborItem; onSave: (b: Partial<LaborItem>) => void; onUp?: () => void }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(item.name);
  const [unit, setUnit] = useState(item.unit);
  const [basis, setBasis] = useState<LaborBasis>(item.basis);
  const [price, setPrice] = useState((item.priceCents / 100).toFixed(2));
  const cents = parseDollars(price);
  if (editing) {
    return (
      <li className="grid grid-cols-2 items-end gap-2 border-b border-mist px-5 py-3 md:grid-cols-[1fr_80px_190px_110px_auto]">
        <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <Field label="Unit" value={unit} onChange={(e) => setUnit(e.target.value)} />
        <SelectField label="Counted by" value={basis} onChange={(e) => setBasis(e.target.value as LaborBasis)}>
          {LABOR_BASES.map((b) => (
            <option key={b} value={b}>
              {LABOR_BASIS_LABELS[b]}
            </option>
          ))}
        </SelectField>
        <Field label="Price" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
        <div className="flex gap-1">
          <Button
            variant="primary"
            disabled={!name.trim() || cents === null || Number.isNaN(cents)}
            onClick={() => {
              onSave({ name, unit, basis, priceCents: cents! });
              setEditing(false);
            }}
          >
            Save
          </Button>
          <Button variant="quiet" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </div>
      </li>
    );
  }
  return (
    <li className={cx("flex items-center gap-3 border-b border-mist px-5 py-3", item.archived && "opacity-60")}>
      <span className="min-w-0 flex-1 font-medium">
        {item.name}
        {item.archived && <span className="ml-2 text-xs text-muted">(retired)</span>}
      </span>
      <span className="text-sm">
        {formatCents(item.priceCents)} / {item.unit}
        {item.basis !== "manual" && <span className="ml-2 text-xs text-muted">{LABOR_BASIS_LABELS[item.basis]}</span>}
      </span>
      {onUp && (
        <button className="text-sm text-muted hover:text-ink" onClick={onUp} aria-label={`Move ${item.name} up`}>
          ↑
        </button>
      )}
      <button className="text-sm font-semibold text-ink-soft hover:text-ink" onClick={() => setEditing(true)}>
        Edit
      </button>
      <button className="text-sm text-muted hover:text-ink" onClick={() => onSave({ archived: !item.archived })}>
        {item.archived ? "Use again" : "Retire"}
      </button>
    </li>
  );
}

/** Weather warnings: which appointment types are checked against the forecast, and when to warn. */
function Weather() {
  const { query, save } = useSettings();
  const [rain, setRain] = useState("");
  const [wind, setWind] = useState("");
  const [types, setTypes] = useState<string[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    const s = query.data?.settings;
    if (s && types === null) {
      setRain(String(s["weather.rainPercent"]));
      setWind(String(s["weather.windMph"]));
      setTypes(s["weather.types"]);
    }
  }, [query.data, types]);
  if (query.isLoading || types === null) return <Loading />;
  const all = [...new Set([...query.data!.settings["lists.appointmentTypes"], ...types])];
  return (
    <Panel>
      <PanelHeader title="Weather warnings" description="Every few hours the National Weather Service forecast is checked for these appointments in the next 6 days. Over a threshold, the appointment shows a warning and the people on it get a task." />
      <form
        className="flex flex-col gap-4 px-5 py-5"
        onSubmit={(e) => {
          e.preventDefault();
          setNotice(null);
          save.mutate({ "weather.rainPercent": Number(rain), "weather.windMph": Number(wind), "weather.types": types }, { onSuccess: () => setNotice("Saved.") });
        }}
      >
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        {notice && <Alert tone="ok">{notice}</Alert>}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Warn at this chance of rain (%)" type="number" min={0} max={100} value={rain} onChange={(e) => setRain(e.target.value)} />
          <Field label="Warn at this wind speed (mph, gusts included)" type="number" min={0} max={150} value={wind} onChange={(e) => setWind(e.target.value)} />
        </div>
        <fieldset>
          <legend className="mb-1 text-sm font-semibold text-ink-soft">Check these appointment types</legend>
          <div className="grid sm:grid-cols-2">
            {all.map((t) => (
              <Checkbox key={t} label={t} checked={types.includes(t)} onChange={(on) => setTypes(on ? [...types, t] : types.filter((x) => x !== t))} />
            ))}
          </div>
        </fieldset>
        <div>
          <Button type="submit" variant="primary" busy={save.isPending}>
            Save
          </Button>
        </div>
      </form>
    </Panel>
  );
}
